'use client';

import { useState } from 'react';
import { ArrowRight, Mail, User } from 'lucide-react';
import { useToast } from '@/components/ui/ToastProvider';

export default function NewsletterForm() {
    const [step, setStep] = useState<1 | 2>(1);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (step === 1) {
            if (!email) return showToast('Please enter your email address', 'error');
            setLoading(true);
            try {
                // Initial subscribe call
                const res = await fetch('/api/newsletter/subscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                });

                if (!res.ok) throw new Error('Failed to subscribe');

                // Move to step 2 for name
                setStep(2);
                showToast('Subscribed! You can optionally add your name.', 'success');
            } catch (error) {
                console.error(error)
                showToast('Failed to subscribe. Please try again.', 'error');
            } finally {
                setLoading(false);
            }
        } else {
            // Step 2: Update with Name
            setLoading(true);
            try {
                const res = await fetch('/api/newsletter/subscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, name })
                });

                if (!res.ok) throw new Error('Failed to update subscription');

                showToast('Subscription completed successfully!', 'success');
                // Reset form
                setEmail('');
                setName('');
                setStep(1);
            } catch (error) {
                console.error(error)
                showToast('Failed to update profile, but you are subscribed!', 'info');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="bg-[#0a192f] p-8 rounded-2xl shadow-lg text-center relative overflow-hidden">
            {/* CSS Pattern Grid Replacement */}
            <div
                className="absolute inset-0 opacity-10"
                style={{
                    backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.8) 1px, transparent 1px)',
                    backgroundSize: '24px 24px'
                }}
            ></div>

            <div className="relative z-10">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                    {step === 1 ? <Mail size={24} /> : <User size={24} />}
                </div>

                <h3 className="font-bold text-white text-xl mb-2">
                    {step === 1 ? 'Subscribe' : 'Almost There!'}
                </h3>

                <p className="text-gray-300 text-sm mb-6">
                    {step === 1
                        ? 'Get the latest news and updates directly to your inbox.'
                        : 'Add your name to personalize your experience (optional).'}
                </p>

                <form onSubmit={handleSubmit} className="space-y-3">
                    {step === 1 ? (
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            disabled={loading}
                            className="w-full px-4 py-3 rounded-xl border border-gray-700 bg-slate-800/50 focus:ring-2 focus:ring-blue-500 outline-none text-sm text-white placeholder:text-gray-500 disabled:opacity-50"
                        />
                    ) : (
                        <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your Name (Optional)"
                                disabled={loading}
                                className="w-full px-4 py-3 rounded-xl border border-gray-700 bg-slate-800/50 focus:ring-2 focus:ring-blue-500 outline-none text-sm text-white placeholder:text-gray-500 disabled:opacity-50"
                            />
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                    >
                        {loading ? (step === 1 ? 'Checking...' : 'Updating...') : (
                            <>
                                {step === 1 ? 'Subscribe Now' : 'Complete Subscription'} <ArrowRight size={16} />
                            </>
                        )}
                    </button>

                    {step === 2 && (
                        <button
                            type="button"
                            onClick={() => {
                                setEmail('');
                                setName('');
                                setStep(1);
                                showToast('Subscription active!', 'success');
                            }}
                            className="text-xs text-gray-400 hover:text-white underline"
                        >
                            Skip for now
                        </button>
                    )}
                </form>
            </div>
        </div>
    );
}
